import { Injectable, inject } from '@angular/core';
import { Observable, of, catchError, tap, map, throwError } from 'rxjs';
import { ApiService } from './api.service';
import { OrderInfo } from '../models';
import { ShipmentAddress } from '../models';
import { OrdersGroupsResponse, SaveOrdersGroupsRequest } from '../models/order.model';

export interface PaginatedInvoices {
  items: any[];
  total: number;
  page: number;
  totalPages: number;
}

/**
 * Country row returned by /api/orders/countries-with-points.
 * Distinct from app.model.Country (countries.db / AMF) because this
 * endpoint enriches each row with EU/VIES + postalMask metadata that the
 * AMF catalog doesn't currently carry.
 */
export interface PickupCountryInfo {
  id: number;
  name: string;
  iso: string;
  isoVies?: string;
  euMember?: number;
  enabled?: number;
  postalMask?: string;
}

/**
 * Order Service - converted from OrderDelegate.as
 *
 * AMFphp Service: OrderService
 * Methods:
 * - addToCart(order, user)
 * - getShipmentMethods(user, locale)
 * - getCountriesWithPoints(user)
 * - getPoints(postalCode, countryId, user)
 * - getStores(user)
 * - getShipments(order, user, locale)
 * - getExtras(order, user, locale)
 * - getCustomerBalance(order, user, locale)
 * - getPayments(order, user, locale)
 * - placeOrder(order, locale, user)
 * - getOrdersGroups(user)
 * - setOrdersGroups(user, orders)
 * - getOrders(status, locale, user, year)
 * - getOrdersDrafts(user)
 * - getOrderDraft(draftId, user)
 * - deleteOrderDraft(draftId, user)
 * - updateOrderFiles(files, user)
 * - getOrderDetails(order, locale, user)
 * - cancelOrder(orderId, reason, user)
 * - getOrderStatus(orderId, user)
 * - getOrderIssue(orderIssue, user, setAsRead)
 * - placeOrderIssue(orderIssue, orderId, user)
 * - placeOrderIssueMessage(orderIssueMessage, orderIssueId, user)
 * - getDreambooksOrders(statuses, locale, user)
 * - getInvoices(user)
 */
@Injectable({
  providedIn: 'root'
})
export class OrderService {
  private readonly api = inject(ApiService);

  /**
   * Get orders
   */
  getOrders(status: string = '', year: number = 0): Observable<any[]> {
    // API expects status as JSON array e.g. [1,2] not comma-separated string
    const statusArray = status ? `[${status}]` : '[]';
    return this.api.get<any>('/orders', { params: { status: statusArray, year } }).pipe(
      map(res => res?.items || res || []),
      map(orders => orders.map((o: any) => ({
        ...o,
        status: o.status ?? o.orderStatus ?? 0
      }))),
      catchError(() => of([]))
    );
  }

  /**
   * Orders from the legacy system (same DB / same PK space as /orders,
   * just a different api_version filter). Identical response shape and
   * param semantics, so the replica-order picker can forkJoin this with
   * getOrders() and merge by id without dedupe logic. Soft-fails to an
   * empty list — the backend already 200/empties on its own DB errors,
   * this catchError is a belt-and-suspenders backstop.
   */
  getLegacyOrders(status: string = '', year: number = 0): Observable<any[]> {
    const statusArray = status ? `[${status}]` : '[]';
    return this.api.get<any>('/orders/legacy', { params: { status: statusArray, year } }).pipe(
      map(res => res?.items || res || []),
      map(orders => orders.map((o: any) => ({
        ...o,
        status: o.status ?? o.orderStatus ?? 0
      }))),
      catchError(() => of([]))
    );
  }

  /**
   * Get order details
   * @param order - Can be a partial order object with orderId
   */
  getOrderDetails(order: any): Observable<any | null> {
    return this.api.get<any>('/orders/' + order.orderId + '/details').pipe(
      map((res: any) => res ? { ...res, status: res.status ?? res.orderStatus ?? 0 } : null),
      catchError(() => of(null))
    );
  }

  /**
   * Get order drafts
   */
  getDrafts(): Observable<any[]> {
    return this.api.get<any>('/drafts').pipe(
      map(response => {
        const items = response?.items || (Array.isArray(response) ? response : []);
        // Map Symfony API field names to the format the UI expects
        return items.map((item: any) => ({
          ...item,
          // UI uses creationDateString / lastModifiedDateString
          creationDateString: item.creationDateString || this.formatApiDate(item.creationDate),
          lastModifiedDateString: item.lastModifiedDateString || this.formatApiDate(item.lastModificationDate),
          // UI uses thumbnail, API returns thumbnailUrl
          thumbnail: item.thumbnail || item.thumbnailUrl || null,
        }));
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Format ISO date string from API to display format (YYYY-MM-DD HH:MM:SS)
   */
  private formatApiDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const pad = (n: number) => n.toString().padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch {
      return dateStr || '';
    }
  }

  /**
   * Get order draft
   */
  getOrderDraft(draftId: number): Observable<any | null> {
    return this.api.get('/drafts/' + draftId).pipe(
      catchError(() => of(null))
    );
  }

  /**
   * Delete order draft
   */
  deleteOrderDraft(draftId: number): Observable<any> {
    return this.api.delete('/drafts/' + draftId).pipe(
      catchError(() => of(null))
    );
  }

  /**
   * Get old orders (Dreambooks)
   */
  getOldOrders(): Observable<OrderInfo[]> {
    return this.api.get<any>('/orders', { params: { type: 'dreambooks' } }).pipe(
      map(res => res?.items || res || []),
      catchError(() => of([]))
    );
  }

  /**
   * Add to cart
   */
  addToCart(order: OrderInfo): Observable<any> {
    const payload = this.cleanOrderPayload(order);
    console.log('[OrderService] addToCart - groups count:', payload.groups?.length);
    return this.api.post('/orders/add-to-cart', payload).pipe(
      map((result: any) => {
        console.log('[OrderService] addToCart - result:', result);
        // Server doesn't echo back projectInfo — merge it from original order
        if (result?.groups && order.groups) {
          const orderGroups: any[] = Array.isArray(order.groups) ? order.groups : Object.values(order.groups);
          const resultGroups: any[] = Array.isArray(result.groups) ? result.groups : Object.values(result.groups);
          for (let gi = 0; gi < resultGroups.length && gi < orderGroups.length; gi++) {
            const resultProducts: any[] = Array.isArray(resultGroups[gi]?.products)
              ? resultGroups[gi].products
              : (resultGroups[gi]?.products ? Object.values(resultGroups[gi].products) : []);
            const orderProducts: any[] = Array.isArray(orderGroups[gi]?.products)
              ? orderGroups[gi].products
              : (orderGroups[gi]?.products ? Object.values(orderGroups[gi].products) : []);
            for (let pi = 0; pi < resultProducts.length && pi < orderProducts.length; pi++) {
              if (orderProducts[pi]?.projectInfo && !resultProducts[pi]?.projectInfo) {
                resultProducts[pi].projectInfo = orderProducts[pi].projectInfo;
                resultProducts[pi].type = orderProducts[pi].type;
              }
            }
          }
        }
        return result;
      }),
      catchError((err) => {
        console.error('[OrderService] addToCart - error:', err);
        return of(null);
      })
    );
  }

  /**
   * Get shipment methods
   */
  getShipmentMethods(): Observable<any[]> {
    return this.api.get<any>('/orders/shipment-methods').pipe(
      map(r => r?.methods || r || []),
      catchError(() => of([]))
    );
  }

  /**
   * Get countries with pickup-point coverage.
   *
   * Returns the full row (id + name + iso + postalMask + …) — the
   * pickup-points form needs more than the ID to drive the placeholder
   * off the backend mask. countries.db (the AMF country catalog) doesn't
   * carry postalMask, so we keep these enriched rows separately.
   *
   * Tolerates plain-ID legacy responses by wrapping them in a stub object
   * with empty name/iso so the dropdown still renders.
   */
  getCountriesWithPoints(): Observable<PickupCountryInfo[]> {
    return this.api.get<any>('/orders/countries-with-points').pipe(
      map(r => {
        const items = r?.data || r || [];
        return items.map((item: any): PickupCountryInfo => {
          if (typeof item !== 'object' || item === null) {
            return { id: Number(item) || 0, name: '', iso: '' };
          }
          return {
            id: Number(item.country_id ?? item.countryId ?? item.id) || 0,
            name: item.name || '',
            iso: item.iso || item.iso_code || '',
            isoVies: item.isoVies,
            euMember: item.euMember,
            enabled: item.enabled,
            postalMask: item.postalMask,
          };
        }).filter((c: PickupCountryInfo) => c.id > 0);
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Get points
   */
  getPoints(postalCode: string, countryId: number): Observable<any[]> {
    return this.api.get<any>('/orders/points', { params: { postalCode, country: countryId } }).pipe(
      map(r => r?.points || r || []),
      catchError(() => of([]))
    );
  }

  /**
   * Get stores
   * Legacy returns StoreInfo with nested StoreShipmentAddress.
   * Symfony API may return flat structure — normalize to nested format.
   */
  getStores(): Observable<any[]> {
    return this.api.get<any>('/orders/stores').pipe(
      map(r => {
        const items = r?.data || r || [];
        return items.map((s: any) => {
          // Already has nested address object — normalize field names
          if (s.address && typeof s.address === 'object') {
            const a = s.address;
            return {
              ...s,
              id: s.id ?? s.storeId ?? s.store_id,
              address: {
                ...a,
                storeId: a.storeId ?? a.store_id ?? s.id ?? s.storeId ?? s.store_id,
                storeName: a.storeName ?? a.store_name ?? s.name,
                postalCode: a.postalCode ?? a.postal_code ?? '',
                city: a.city ?? a.city_id ?? a.cityId ?? null,
                state: a.state ?? a.state_id ?? a.stateId ?? null,
                country: a.country ?? a.country_id ?? a.countryId ?? 1,
              }
            };
          }
          // Flat format from Symfony raw SQL (snake_case) — build nested StoreShipmentAddress
          const storeId = s.store_id ?? s.storeId ?? s.id;
          return {
            id: storeId,
            name: s.name,
            email: s.email || '',
            phone: s.phone || s.telephone || '',
            mPhone: s.mPhone || s.mobile_phone || '',
            image: s.image || s.url || '',
            open1: s.open1 || s.open_hour1 || '',
            close1: s.close1 || s.close_hour1 || '',
            open2: s.open2 || s.open_hour2 || '',
            close2: s.close2 || s.close_hour2 || '',
            hours: s.hours || [],
            latitude: s.latitude,
            longitude: s.longitude,
            address: {
              id: 0,
              storeId: storeId,
              storeName: s.name,
              name: s.name,
              address: s.address || '',
              local: s.local || '',
              postalCode: s.postal_code ?? s.postalCode ?? '',
              city: s.city_id ?? s.cityId ?? null,
              state: s.state_id ?? s.stateId ?? null,
              country: s.country_id ?? s.countryId ?? 1
            }
          };
        });
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Get shipments.
   *
   * Errors are NOT swallowed: the backend answers business refusals here with
   * `{error, code}` — notably `SHIPMENT_COUNTRY_NOT_ALLOWED` (ERROR_6541),
   * which the cart must explain next to the address instead of silently
   * showing an empty shipping list. Callers handle the error; the HTTP layer
   * has already dealt with auth/refresh.
   */
  getShipments(order: OrderInfo): Observable<any[]> {
    return this.api.post<any>('/orders/shipments', this.cleanOrderPayload(order)).pipe(
      map(r => r?.shipments || r || [])
    );
  }

  /**
   * Get extras
   */
  getExtras(order: OrderInfo): Observable<any[]> {
    return this.api.post<any>('/orders/shipments/extras', this.cleanOrderPayload(order)).pipe(
      map(r => r?.extras || r || []),
      catchError(() => of([]))
    );
  }

  /**
   * Get customer balance
   */
  getCustomerBalance(order: OrderInfo): Observable<any> {
    return this.api.get<any>('/orders/customer-balance/' + (order as any).customerId).pipe(
      map(r => r?.data || r),
      catchError(() => of(null))
    );
  }

  /**
   * Get payment methods
   */
  getPayments(order: OrderInfo): Observable<any[]> {
    return this.api.post<any>('/orders/payments', this.cleanOrderPayload(order)).pipe(
      map(r => r?.payments || r || []),
      catchError(() => of([]))
    );
  }

  /**
   * Place order
   */
  placeOrder(order: OrderInfo): Observable<any> {
    const payload = this.cleanOrderPayload(order);
    console.log('[OrderService] placeOrder - code:', payload.code, 'groups:', payload.groups?.length);
    return this.api.post('/orders/place', payload).pipe(
      tap(result => console.log('[OrderService] placeOrder result:', result)),
      catchError(err => {
        console.error('[OrderService] placeOrder error:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Get orders groups. Returns either the group array or a status object
   * ({status:'UNAVAILABLE'|'ERROR_WS_CONNECTION'|...}). A 502 carries
   * {status:'ERROR_WS_CONNECTION'} in the error body; surface it as a status
   * object so callers can branch instead of treating it as an empty list.
   */
  getOrdersGroups(): Observable<OrdersGroupsResponse> {
    return this.api.get<any>('/orders/groups').pipe(
      map(r => this.normalizeGroupsResponse(r)),
      catchError(err => of(err?.error?.status ? { status: err.error.status } : { status: 'ERROR_WS_CONNECTION' }))
    );
  }

  /**
   * The groups endpoints always return a JSON object now: {groups:[...]} on
   * success or {status:'...'} otherwise (both also carry an ignorable _token).
   * Normalize to the array (success) or a {status} object so callers can keep
   * the simple Array.isArray vs .status branch.
   */
  private normalizeGroupsResponse(r: any): OrdersGroupsResponse {
    const groups = r?.groups ?? r?.data?.groups;
    if (Array.isArray(groups)) return groups;
    const status = r?.status ?? r?.data?.status;
    return status ? { status } : [];
  }

  /**
   * Save the complete desired set of order groups. Returns the refreshed
   * group list (array) on success, or a status object on rejection. Carries
   * the same array-vs-status handling as getOrdersGroups.
   */
  setOrdersGroups(groups: SaveOrdersGroupsRequest['groups']): Observable<OrdersGroupsResponse> {
    return this.api.post<any>('/orders/groups', { groups }).pipe(
      map(r => this.normalizeGroupsResponse(r)),
      catchError(err => of(err?.error?.status ? { status: err.error.status } : { status: 'ERROR_WS_CONNECTION' }))
    );
  }

  /**
   * Cancel order
   */
  cancelOrder(orderId: number, reason: string = ''): Observable<any> {
    return this.api.post('/orders/' + orderId + '/cancel', { reason }).pipe(
      catchError(() => of(false))
    );
  }

  /**
   * Get order status
   */
  getOrderStatus(orderId: number): Observable<any> {
    return this.api.get('/orders/' + orderId + '/details').pipe(
      catchError(() => of(null))
    );
  }

  /**
   * Place order issue
   */
  placeOrderIssue(orderIssue: any, orderId: number): Observable<any> {
    return this.api.post('/orders/' + orderId + '/cancel', orderIssue).pipe(
      catchError(() => of(null))
    );
  }

  /**
   * Get order issue
   */
  getOrderIssue(orderIssue: any, setAsRead: number = 1): Observable<any> {
    return this.api.get('/orders/' + orderIssue.orderId + '/details', { params: { setAsRead } }).pipe(
      catchError(() => of(null))
    );
  }

  /**
   * Get invoices (paginated)
   */
  getInvoices(params?: { page?: number; limit?: number; year?: number; search?: string }): Observable<PaginatedInvoices> {
    return this.api.get<any>('/invoices', { params: { ...params } }).pipe(
      map(res => ({
        items: res?.items || [],
        total: res?.pagination?.total ?? res?.total ?? 0,
        page: res?.pagination?.current_page ?? res?.page ?? 1,
        totalPages: res?.pagination?.total_pages ?? res?.totalPages ?? 1
      })),
      catchError(() => of({ items: [], total: 0, page: 1, totalPages: 1 }))
    );
  }

  // Legacy methods (kept for compatibility)
  createOrder(shippingAddress: ShipmentAddress, paymentMethod: string): Observable<OrderInfo | null> {
    // Use placeOrder instead
    return of(null);
  }

  reorder(orderId: number): Observable<boolean> {
    // Would need to get order details and addToCart
    return of(false);
  }

  reportIssue(orderId: number, issue: string, images?: string[]): Observable<boolean> {
    const orderIssue = { description: issue, images };
    return this.placeOrderIssue(orderIssue, orderId);
  }

  groupOrders(orderIds: number[]): Observable<OrderInfo | null> {
    // Use setOrdersGroups
    return of(null);
  }

  /**
   * Map a frontend finish object to the shape the Symfony API expects.
   * Uses ?? (nullish coalescing) instead of || so that id: 0 is preserved as a valid finishId.
   */
  private mapFinishForApi(f: any): { optionId: number; finishId: number | null; value: any } {
    return {
      optionId: f.optionId ?? 0,
      finishId: f.id ?? null,
      value: f.data ?? null,
    };
  }

  /**
   * Clean order payload for Symfony forms.
   * - Strips extra fields from finishes (FinishOptionType only accepts optionId, finishId, value)
   * - Converts checkbox-like integer values to strings for CheckboxType fields
   */
  private cleanOrderPayload(order: OrderInfo): any {
    const raw = order as any;
    const cleaned: any = { ...raw };

    // --- Strip server-only output fields ---
    const stripFields = [
      '_explicitType', '_token', 'orderTotals', 'allowUrgent',
      'version', 'shipmentTrackings', 'warning', 'issues',
      'estimatedDeliveryDate', 'orderDate'
    ];
    stripFields.forEach(f => delete cleaned[f]);

    // Convert top-level checkbox fields to string '0'/'1'
    if (cleaned.urgent != null) cleaned.urgent = cleaned.urgent ? 1 : 0;
    if (cleaned.showVat != null) cleaned.showVat = cleaned.showVat ? 1 : 0;

    // --- Remove billingAddress if empty (all nulls/empty strings) ---
    if (cleaned.billingAddress) {
      const ba = cleaned.billingAddress;
      const hasValue = Object.values(ba).some(v => v != null && v !== '');
      if (!hasValue) {
        delete cleaned.billingAddress;
      }
    }

    // --- Normalize vouchers to the canonical { voucherId } envelope ---
    // The server reads `vouchers: [{ voucherId }]` and DERIVES its internal
    // voucherCodes from it, OVERWRITING any voucherCodes we send (empty when no
    // vouchers[] is present). So we must emit the envelope — sending the legacy
    // flat voucherCodes/voucherCode would get the applied vouchers silently
    // wiped. Extra keys on each entry are ignored by the server.
    if (Array.isArray(cleaned.vouchers)) {
      cleaned.vouchers = cleaned.vouchers
        .map((v: any) => {
          const voucherId = v?.voucherId || v?.code || (typeof v === 'string' ? v : null);
          return voucherId != null ? { voucherId } : null;
        })
        .filter((v: any) => v != null);
    }
    // Never send the legacy flat shapes — the server ignores/overwrites them.
    delete cleaned.voucherCodes;
    delete cleaned.voucherCode;

    // --- Normalize extras to integer IDs ---
    if (Array.isArray(cleaned.extras)) {
      cleaned.extras = cleaned.extras.map((e: any) => {
        const id = typeof e === 'object' ? (e.id ?? e) : e;
        return typeof id === 'string' ? parseInt(id, 10) : id;
      });
    }

    // Clean groups — backend may return an object (keyed by index) or an array
    const cleanGroup = (group: any) => {
      const g = { ...group };
      // Convert group checkbox fields to string '0'/'1'
      if (g.showcase != null) g.showcase = g.showcase ? '1' : '0';
      if (g.showcaseEnabled != null) g.showcaseEnabled = g.showcaseEnabled ? '1' : '0';
      if (g.showPrices != null) g.showPrices = g.showPrices ? '1' : '0';

      // Clean products within this group
      if (Array.isArray(g.products)) {
        g.products = g.products.map((product: any) => {
          const p = { ...product };
          // Strip extra fields from finishes — FinishOptionType only accepts optionId, finishId, value
          if (Array.isArray(p.finishes)) {
            p.finishes = p.finishes.map((f: any) => this.mapFinishForApi(f));
          }
          // Map projectInfo.id → project (Symfony EntityType expects integer ID)
          if (p.projectInfo?.id) {
            p.project = p.projectInfo.id;
          }
          delete p.projectInfo;
          // Fallback: preserve project from stored cart data
          if (!p.project && p.projectId && typeof p.projectId === 'number') {
            p.project = p.projectId;
          }
          delete p.projectId;
          return p;
        });
      }
      return g;
    };

    if (Array.isArray(cleaned.groups)) {
      cleaned.groups = cleaned.groups.map(cleanGroup);
    } else if (cleaned.groups && typeof cleaned.groups === 'object') {
      // groups is an object keyed by index (e.g. {2: {...}, 3: {...}})
      const cleanedGroups: any = {};
      for (const key of Object.keys(cleaned.groups)) {
        cleanedGroups[key] = cleanGroup(cleaned.groups[key]);
      }
      cleaned.groups = cleanedGroups;
    }

    const toInt = (v: any): number | null => {
      if (v == null) return null;
      if (typeof v === 'object') return v.id ?? v.cityId ?? v.stateId ?? v.countryId ?? null;
      const n = parseInt(v, 10);
      return isNaN(n) ? null : n;
    };

    // Normalize shipmentType: API expects integer, frontend stores object
    if (cleaned.shipmentType && typeof cleaned.shipmentType === 'object') {
      cleaned.shipmentType = toInt(cleaned.shipmentType.shipmentTypeId) ?? toInt(cleaned.shipmentType.id);
    }
    // Normalize shipmentMethod: API expects integer, frontend stores object
    if (cleaned.shipmentMethod && typeof cleaned.shipmentMethod === 'object') {
      cleaned.shipmentMethod = toInt(cleaned.shipmentMethod.id);
    }
    // Normalize payment: API expects integer ID, frontend stores object
    if (cleaned.payment && typeof cleaned.payment === 'object') {
      cleaned.payment = toInt(cleaned.payment.id);
    }
    // Normalize shipmentAddress integer fields (may be objects, strings, or ints)
    if (cleaned.shipmentAddress && typeof cleaned.shipmentAddress === 'object') {
      const addr = cleaned.shipmentAddress;
      addr.id = toInt(addr.id);
      addr.city = toInt(addr.city);
      addr.state = toInt(addr.state);
      addr.country = toInt(addr.country);
    }

    return cleaned;
  }
}
